import { Controller } from "@hotwired/stimulus"
import { errorDetail, formatDate, getAllJSON, getJSON, sendJSON } from "../api.js"
import { setComboOptions, comboValue } from "../combobox_helpers.js"
import { getActLabel, setActLabel } from "../icons.js"

// Client-facing labels of Organization.Status (organizations/models/organization.py).
const ORG_STATUS_LABELS = { approved: "Zatwierdzona", pending: "Oczekująca" }

// Konto: profil (PATCH me) + organizacje (read) + zespół/zaproszenia (create/accept).
// Zapraszać mogą owner/admin — 403 z API pokazujemy jako komunikat, nie ukrywamy formularza
// (uprawnienia egzekwuje serwer).
export default class extends Controller {
  static targets = ["nameInput", "profileSaved",
                    "orgRows", "orgStatus",
                    "inviteOrgSelect", "inviteEmail", "inviteRole", "inviteError",
                    "inviteRows", "inviteStatus", "acceptToken", "acceptMessage",
                    "apiTokenRow", "apiTokenBtn", "apiTokenNote", "inviteBtn", "acceptBtn"]
  static values = {
    meUrl: String,            // /api/users/me/
    organizationsUrl: String, // /api/organizations/
    invitationsUrl: String,   // /api/invitations/
    apiTokenUrl: String,      // /api/me/api-token/ (GET {has_token}; POST revoke/) — B-55
  }

  static INVITE_BADGE = {
    pending:  ["warm", "oczekuje"],
    accepted: ["sent", "przyjęte"],
    revoked:  ["new",  "odwołane"],
    expired:  ["hot",  "wygasłe"],
  }

  static ROLE_LABEL = { owner: "Właściciel", admin: "Administrator", member: "Członek" }

  async connect() {
    try {
      this.me = await getJSON(this.meUrlValue) // {name, url} — url = cel PATCH profilu
    } catch {
      this.me = null
    }
    await Promise.all([this.#loadOrgs(), this.loadInvitations(), this.#loadApiTokenStatus()])
  }

  // B-55: two-click confirm (no browser confirm()), then POST revoke/; the row hides once revoked.
  async revokeApiToken() {
    const btn = this.apiTokenBtnTarget
    if (!btn.dataset.confirmPending) {
      btn.dataset.confirmPending = "1"
      btn.dataset.originalText = getActLabel(btn)
      setActLabel(btn, "Na pewno? Kliknij ponownie")
      btn._confirmTimeout = setTimeout(() => {
        delete btn.dataset.confirmPending
        setActLabel(btn, btn.dataset.originalText)
      }, 4000)
      return
    }
    clearTimeout(btn._confirmTimeout)
    delete btn.dataset.confirmPending
    btn.disabled = true
    const res = await sendJSON("POST", `${this.apiTokenUrlValue}revoke/`)
    btn.disabled = false
    setActLabel(btn, btn.dataset.originalText)
    if (res.ok) {
      this.apiTokenRowTarget.hidden = true
      return
    }
    this.#apiTokenNote(res.status === 404
      ? "Token API został już unieważniony."
      : errorDetail(res.data, "Nie udało się unieważnić tokenu API."))
    if (res.status === 404) this.apiTokenRowTarget.hidden = true
  }

  async saveProfile() {
    if (!this.me?.url) return
    const res = await sendJSON("PATCH", this.me.url, { name: this.nameInputTarget.value.trim() })
    if (this.hasProfileSavedTarget) this.profileSavedTarget.hidden = !res.ok
  }

  async invite() {
    if (this.inviting) return
    const organization = comboValue(this.hasInviteOrgSelectTarget ? this.inviteOrgSelectTarget : null)
    const email = this.inviteEmailTarget.value.trim()
    if (!organization || !email) {
      this.#inviteError("Wybierz organizację i podaj e-mail.")
      return
    }
    const role = this.hasInviteRoleTarget
      ? (this.inviteRoleTarget.querySelector('input[type="radio"]:checked')?.value || "member")
      : "member"
    this.inviting = true
    if (this.hasInviteBtnTarget) this.inviteBtnTarget.disabled = true
    const res = await sendJSON("POST", this.invitationsUrlValue, {
      organization: Number(organization),
      email,
      role,
    })
    this.inviting = false
    if (this.hasInviteBtnTarget) this.inviteBtnTarget.disabled = false
    if (!res.ok) {
      this.#inviteError(errorDetail(res.data, res.status === 403
        ? "Zapraszać mogą właściciel lub administrator organizacji."
        : "Nie udało się wysłać zaproszenia."))
      return
    }
    this.#inviteError(null)
    this.inviteEmailTarget.value = ""
    await this.loadInvitations()
  }

  async accept() {
    if (this.accepting) return
    const token = this.acceptTokenTarget.value.trim()
    if (!token) return
    this.accepting = true
    if (this.hasAcceptBtnTarget) this.acceptBtnTarget.disabled = true
    const res = await sendJSON("POST", `${this.invitationsUrlValue}accept/`, { token })
    this.accepting = false
    if (this.hasAcceptBtnTarget) this.acceptBtnTarget.disabled = false
    if (this.hasAcceptMessageTarget) {
      this.acceptMessageTarget.hidden = false
      if (res.ok) {
        this.acceptMessageTarget.style.color = "var(--color-ls-green)"
        this.acceptMessageTarget.textContent = "Dołączono do organizacji ✓"
      } else {
        this.acceptMessageTarget.style.color = "var(--color-ls-red)"
        this.acceptMessageTarget.textContent = errorDetail(res.data, "Token niepoprawny lub wygasły.")
      }
    }
    if (res.ok) {
      this.acceptTokenTarget.value = ""
      await this.#loadOrgs()
    }
  }

  // LS2: telefon kontaktowy firmy (PATCH /api/organizations/{id}/) — pokazywany osobom, do których firma dzwoni po
  // zakupie leada. Zmieniać mogą owner/admin; zwykły członek dostaje 403, które pokazujemy jako komunikat.
  async saveOrgPhone(event) {
    const row = event.currentTarget.closest("[data-org-id]")
    if (!row || row.dataset.phoneSaving) return
    const input = row.querySelector('[data-field="phone"]')
    const button = row.querySelector('[data-field="phoneSave"]')
    const saved = row.querySelector('[data-field="phoneSaved"]')
    const error = row.querySelector('[data-field="phoneError"]')
    row.dataset.phoneSaving = "1"
    if (button) button.disabled = true
    if (saved) saved.hidden = true
    if (error) error.hidden = true
    const res = await sendJSON("PATCH", `${this.organizationsUrlValue}${row.dataset.orgId}/`, {
      contact_phone: input.value.trim(),
    })
    delete row.dataset.phoneSaving
    if (button) button.disabled = false
    if (res.ok) {
      input.value = res.data?.contact_phone ?? input.value.trim()
      if (saved) saved.hidden = false
      return
    }
    if (error) {
      error.textContent = errorDetail(res.data, "Nie udało się zapisać telefonu kontaktowego.")
      error.hidden = false
    }
  }

  async loadInvitations() {
    let invitations
    try {
      invitations = await getAllJSON(this.invitationsUrlValue)
    } catch {
      this.#inviteStatus("Nie udało się załadować zaproszeń.")
      return
    }
    this.inviteRowsTarget.textContent = ""
    if (!invitations.length) {
      this.#inviteStatus("Brak zaproszeń (widoczne dla właściciela/administratora organizacji).")
      return
    }
    this.#inviteStatus(null)
    const template = document.getElementById("invite-row-template")
    for (const invitation of invitations) {
      const row = template.content.firstElementChild.cloneNode(true)
      row.querySelector('[data-field="email"]').textContent = invitation.email
      row.querySelector('[data-field="role"]').textContent =
        this.constructor.ROLE_LABEL[invitation.role] ?? invitation.role
      const status = invitation.status === "pending" && Date.parse(invitation.expires_at) < Date.now()
        ? "expired" : invitation.status
      const [kind, label] = this.constructor.INVITE_BADGE[status] ?? ["new", status]
      const badge = row.querySelector('[data-field="badge"]')
      badge.className = `ls-badge ls-badge--${kind}`
      badge.textContent = label
      row.querySelector('[data-field="expires"]').textContent = formatDate(invitation.expires_at)
      this.inviteRowsTarget.appendChild(row)
    }
  }

  // --- prywatne ---

  async #loadApiTokenStatus() {
    if (!this.hasApiTokenUrlValue || !this.hasApiTokenRowTarget) return
    try {
      const status = await getJSON(this.apiTokenUrlValue)
      this.apiTokenRowTarget.hidden = !status.has_token
    } catch {
      this.apiTokenRowTarget.hidden = true
    }
  }

  #apiTokenNote(message) {
    if (!this.hasApiTokenNoteTarget) return
    this.apiTokenNoteTarget.hidden = false
    this.apiTokenNoteTarget.textContent = message
  }

  async #loadOrgs() {
    let organizations
    try {
      organizations = await getAllJSON(this.organizationsUrlValue)
    } catch {
      this.#orgStatus("Nie udało się załadować organizacji.")
      return
    }
    this.orgRowsTarget.textContent = ""
    if (!organizations.length) {
      this.#orgStatus("Nie należysz do żadnej organizacji.")
      return
    }
    this.#orgStatus(null)
    const template = document.getElementById("org-row-template")
    const comboOpts = []
    for (const org of organizations) {
      const row = template.content.firstElementChild.cloneNode(true)
      row.querySelector('[data-field="name"]').textContent = org.name
      row.dataset.orgId = String(org.id)
      const phone = row.querySelector('[data-field="phone"]')
      if (phone) {
        phone.value = org.contact_phone || ""
        const label = row.querySelector('[data-field="phoneLabel"]')
        const id = `org-phone-${org.id}`
        phone.id = id
        if (label) label.setAttribute("for", id)
      }
      const badge = row.querySelector('[data-field="badge"]')
      // Organization.Status is pending | approved (the old "active" check never matched).
      const approved = org.status === "approved"
      badge.className = `ls-badge ${approved ? "ls-badge--sent" : "ls-badge--new"}`
      badge.textContent = ORG_STATUS_LABELS[org.status] || org.status
      this.orgRowsTarget.appendChild(row)
      comboOpts.push({ value: org.id, label: org.name })
    }
    if (this.hasInviteOrgSelectTarget) {
      await setComboOptions(this.application, this.inviteOrgSelectTarget, comboOpts)
    }
  }

  #orgStatus(message) {
    if (!this.hasOrgStatusTarget) return
    this.orgStatusTarget.hidden = message === null
    if (message !== null) this.orgStatusTarget.textContent = message
  }

  #inviteStatus(message) {
    if (!this.hasInviteStatusTarget) return
    this.inviteStatusTarget.hidden = message === null
    if (message !== null) this.inviteStatusTarget.textContent = message
  }

  #inviteError(message) {
    if (!this.hasInviteErrorTarget) return
    this.inviteErrorTarget.hidden = message === null
    if (message !== null) this.inviteErrorTarget.textContent = message
  }
}
